import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useState } from 'react';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import FormTextField from '../../../shared/FormTextField';
import { ModalProps } from '../../../types/modal';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import FormSelect from '../../../shared/FormSelect';
import { ExperienceCharge } from '../../../types/experience';
import { BasePriceSchema } from '../Form/Pricing/schema';
import { FeeType } from '../../../enums/experience';

export type Pricing = {
    prices: Array<ExperienceCharge>;
};

interface Props extends ModalProps {
    experienceType: string;
    availableSpaces: any[];
    prices: ExperienceCharge[];
    minHireHours?: number;
    onSubmit: (data: Pricing) => void;
    isEditing: boolean;
}

const getType = (id: string, duration: 'day' | 'halfDay' = 'day') => {
    switch (id) {
        case 'priceDesk':
            return duration === 'halfDay'
                ? FeeType.PerDeskPerHalfDay
                : FeeType.PerDeskPerDay;
        case 'priceMeetingRoom':
            return duration === 'halfDay'
                ? FeeType.PerMeetingRoomPerHalfDay
                : FeeType.PerMeetingRoomPerDay;
        case 'priceOffice':
            return duration === 'halfDay'
                ? FeeType.PerOfficePerHalfDay
                : FeeType.PerOfficePerDay;
        default:
            return duration === 'halfDay'
                ? FeeType.EntireVenuePerHalfDay
                : FeeType.EntireVenuePerDay;
    }
};

const BasePriceModal = ({
    experienceType,
    availableSpaces,
    open,
    prices,
    minHireHours,
    onClose,
    onSubmit,
    isEditing,
}: Props) => {
    const isMobile = useMediaQuery('(max-width: 700px)');

    const [cancel, setCancel] = useState<boolean>(false);

    const shouldShowHalfDayBilling = minHireHours && minHireHours < 24;

    const defaultValues: Pricing = {
        prices:
            experienceType !== 'commercial'
                ? [
                      {
                          feeId: '',
                          type: FeeType.PerDay,
                          unitPrice: 0,
                          chargeType: 'price',
                          weekendPrice: null,
                      },
                  ]
                : availableSpaces.flatMap((space: any) => {
                      return space.type === FeeType.PerDeskPerDay ||
                          !shouldShowHalfDayBilling
                          ? [
                                {
                                    feeId: space.feeId,
                                    type: getType(space.feeId, 'day'),
                                    unitPrice: 0,
                                    chargeType: 'price',
                                },
                            ]
                          : [
                                {
                                    feeId: space.feeId,
                                    type: getType(space.feeId, 'day'),
                                    unitPrice: 0,
                                    chargeType: 'price',
                                },
                                {
                                    feeId: space.feeId,
                                    type: getType(space.feeId, 'halfDay'),
                                    unitPrice: 0,
                                    chargeType: 'price',
                                },
                            ];
                  }),
    };
    useEffect(() => {
        reset(defaultValues);
    }, [experienceType, availableSpaces]);

    useEffect(() => {
        if (prices.length > 0) reset({ prices: prices });
    }, [prices, isEditing]);

    const methods = useForm<Pricing>({
        resolver: zodResolver(BasePriceSchema),
        defaultValues: defaultValues,
    });

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { isDirty },
    } = methods;

    const weekendPrice = useWatch({ control, name: 'prices.0.weekendPrice' });

    const handleClearWeekendPrice = () => {
        setValue('prices.0.weekendPrice.unitPrice', 0);
        setValue('prices.0.weekendPrice.days', []);
    };

    useEffect(() => {
        if (experienceType !== 'commercial') return;
        // reorder prices to align with availableSpaces
        const syncedPrices: ExperienceCharge[] = availableSpaces.flatMap(
            (space: any) => {
                const dayType = getType(space.feeId, 'day');
                const halfDayType = getType(space.feeId, 'halfDay');
                const existingDayPrice = prices.find(
                    (price) =>
                        price.chargeType !== 'flexiblePrice' &&
                        'type' in price &&
                        (price.type === dayType ||
                            (space.feeId === 'priceEntireVenue' &&
                                price.type === FeeType.EntireVenuePerNight)),
                );
                const existingHalfDayPrice = prices.find(
                    (price) =>
                        price.chargeType !== 'flexiblePrice' &&
                        'type' in price &&
                        price.type === halfDayType,
                );

                if (dayType === FeeType.PerDeskPerDay) {
                    return [
                        existingDayPrice || {
                            feeId: space.feeId,
                            type: dayType,
                            unitPrice: 0,
                            chargeType: 'price',
                        },
                    ];
                }

                if (shouldShowHalfDayBilling) {
                    return [
                        existingDayPrice || {
                            feeId: space.feeId,
                            type: dayType,
                            unitPrice: 0,
                            chargeType: 'price',
                        },
                        existingHalfDayPrice || {
                            feeId: space.feeId,
                            type: halfDayType,
                            unitPrice: 0,
                            chargeType: 'price',
                        },
                    ];
                }

                return [
                    existingDayPrice || {
                        feeId: space.feeId,
                        type: dayType,
                        unitPrice: 0,
                        chargeType: 'price',
                    },
                ];
            },
        );
        // Reset the form with the updated prices
        reset({ prices: syncedPrices });
    }, [availableSpaces, prices, reset, experienceType]);

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        return handleSubmit(onSubmit)(event);
    };

    const handleCheck = () => {
        if (weekendPrice !== null) {
            setValue('prices.0.weekendPrice', null);
        } else {
            setValue('prices.0.weekendPrice.unitPrice', 0);
            setValue('prices.0.weekendPrice.days', []);
        }
    };

    const handleCancel = () => {
        if (isDirty) {
            // if form is dirty, display alert
            if (!cancel) {
                setCancel(true);
                return;
            }
        }

        if (cancel) reset();

        setCancel(false);
        onClose();
    };
    let runningPriceIndex = 0;

    const indexedCommercialSpaces = availableSpaces.map((space: any) => {
        const requiresHalfDay =
            space.type !== FeeType.PerDeskPerDay && shouldShowHalfDayBilling;
        const dayIndex = runningPriceIndex;
        const halfDayIndex = requiresHalfDay ? runningPriceIndex + 1 : null;

        runningPriceIndex += requiresHalfDay ? 2 : 1;

        return {
            space,
            dayIndex,
            halfDayIndex,
        };
    });

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth='md' fullWidth>
            <FormProvider {...methods}>
                <DialogTitle>
                    Base Price
                    <IconButton
                        onClick={handleCancel}
                        size='small'
                        aria-label='close'
                        sx={{ color: 'text.primary' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <form id='base-price-modal-form' onSubmit={onNestedSubmit}>
                        <DialogContentText
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: 'text.primary',
                                fontSize: '14px',
                            }}
                        >
                            {experienceType === 'commercial'
                                ? 'Applied to all dates. All prices are in AUD.'
                                : 'Applied to all dates unless you customise. Note: all prices are excluding GST and in AUD.'}
                        </DialogContentText>
                        <Collapse in={cancel}>
                            <Alert severity='warning' sx={{ mb: 2 }}>
                                You have unsaved changes. Are you sure you want
                                to cancel?
                            </Alert>
                        </Collapse>
                        {experienceType !== 'commercial' && (
                            <>
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '20px',
                                    }}
                                >
                                    <Grid
                                        container
                                        columnSpacing={2}
                                        rowSpacing={1}
                                    >
                                        <Grid item container xs={12} lg={6}>
                                            <Grid item xs={12} lg={4}>
                                                <InputLabel
                                                    required
                                                    htmlFor='unit-price__input'
                                                >
                                                    Amount
                                                </InputLabel>
                                            </Grid>
                                            <Grid item xs={12} lg={8}>
                                                <FormTextField
                                                    id='unit-price__input'
                                                    name='prices.0.unitPrice'
                                                    type='number'
                                                    control={control}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position='start'>
                                                                <Typography>
                                                                    $
                                                                </Typography>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item container xs={12} lg={6}>
                                            <Grid item xs={12} lg={4}>
                                                <InputLabel
                                                    required
                                                    id='fee-type__label'
                                                    htmlFor='fee-type__input'
                                                >
                                                    Type
                                                </InputLabel>
                                            </Grid>
                                            <Grid item xs={12} lg={8}>
                                                <FormSelect
                                                    id='fee-type__input'
                                                    labelId='fee-type__label'
                                                    name='prices.0.type'
                                                    control={control}
                                                    options={[
                                                        {
                                                            value: FeeType.PerNight,
                                                            label: 'Per Night',
                                                        },
                                                        {
                                                            value: FeeType.PerDay,
                                                            label: 'Per Day',
                                                        },
                                                        {
                                                            value: FeeType.PerNightPerGuest,
                                                            label: 'Per Night Per Guest',
                                                        },
                                                        {
                                                            value: FeeType.PerDayPerGuest,
                                                            label: 'Per Day Per Guest',
                                                        },
                                                        {
                                                            value: FeeType.PerHour,
                                                            label: 'Per Hour',
                                                        },
                                                        {
                                                            value: FeeType.PerHourPerGuest,
                                                            label: 'Per Hour Per Guest',
                                                        },
                                                    ]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                    >
                                        <Stack spacing='3px'>
                                            <Typography
                                                color='textPrimary'
                                                fontWeight='bold'
                                            >
                                                Custom Pricing
                                            </Typography>
                                            <Typography
                                                component='label'
                                                color='textPrimary'
                                                fontSize='12px'
                                                htmlFor='weekend-price__input'
                                            >
                                                Add a custom weekend price
                                            </Typography>
                                        </Stack>
                                        <Checkbox
                                            id='weekend-price__input'
                                            checked={
                                                weekendPrice !== null && true
                                            }
                                            onChange={handleCheck}
                                        />
                                    </Box>
                                    {weekendPrice !== null && (
                                        <>
                                            <FormHelperText>
                                                Base price will be overridden by
                                                the weekend price on the days
                                                you select.
                                            </FormHelperText>
                                            <Grid container columnSpacing={2}>
                                                <Grid
                                                    item
                                                    container
                                                    xs={12}
                                                    lg={6}
                                                >
                                                    <Grid item xs={12} lg={4}>
                                                        <InputLabel htmlFor='weekend-price-amount__input'>
                                                            Amount
                                                        </InputLabel>
                                                    </Grid>
                                                    <Grid item xs={12} lg={8}>
                                                        <FormTextField
                                                            id='weekend-price-amount__input'
                                                            name='prices.0.weekendPrice.unitPrice'
                                                            control={control}
                                                            type='number'
                                                            InputProps={{
                                                                startAdornment:
                                                                    (
                                                                        <InputAdornment position='start'>
                                                                            <Typography>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    item
                                                    container
                                                    xs={12}
                                                    lg={6}
                                                >
                                                    <Grid item xs={12} lg={4}>
                                                        <InputLabel htmlFor='weekend-price-days__input'>
                                                            Days
                                                        </InputLabel>
                                                    </Grid>
                                                    <Grid item xs={12} lg={8}>
                                                        <FormMultiAutocomplete
                                                            id='weekend-price-days__input'
                                                            name='prices.0.weekendPrice.days'
                                                            control={control}
                                                            options={[
                                                                {
                                                                    value: 'friday',
                                                                    label: 'Friday',
                                                                },
                                                                {
                                                                    value: 'saturday',
                                                                    label: 'Saturday',
                                                                },
                                                                {
                                                                    value: 'sunday',
                                                                    label: 'Sunday',
                                                                },
                                                            ]}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </>
                        )}
                        {experienceType === 'commercial' && (
                            <Grid
                                container
                                columns={25}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                }}
                            >
                                {indexedCommercialSpaces.map(
                                    (
                                        { space, dayIndex, halfDayIndex },
                                        spaceIndex: number,
                                    ) => {
                                        return (
                                            <Grid
                                                key={`${space.feeId}-${spaceIndex}`}
                                                container
                                                item
                                                spacing={1}
                                            >
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={
                                                        shouldShowHalfDayBilling
                                                            ? 3
                                                            : 4
                                                    }
                                                >
                                                    <InputLabel
                                                        htmlFor={`${space.feeId}__input`}
                                                    >
                                                        {camelCaseToTitleCase(
                                                            space.spaceType,
                                                        )}
                                                    </InputLabel>
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md
                                                    sx={{
                                                        display: 'flex',
                                                        width: isMobile
                                                            ? 'auto'
                                                            : '290px',
                                                        gap: '5px',
                                                    }}
                                                >
                                                    <FormControl
                                                        size='small'
                                                        fullWidth={true}
                                                    >
                                                        <FormTextField
                                                            id={`${space.feeId}__input`}
                                                            name={`prices[${dayIndex}].unitPrice`}
                                                            control={control}
                                                            type='number'
                                                            InputProps={{
                                                                startAdornment:
                                                                    (
                                                                        <InputAdornment position='start'>
                                                                            $
                                                                        </InputAdornment>
                                                                    ),
                                                                endAdornment:
                                                                    space.feeId !==
                                                                        'priceEntireVenue' && (
                                                                        <InputAdornment position='end'>
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                }}
                                                                            >
                                                                                Per
                                                                                Day
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                            }}
                                                        />
                                                    </FormControl>
                                                    {space.feeId ===
                                                        'priceEntireVenue' && (
                                                        <Grid
                                                            sx={{
                                                                minWidth:
                                                                    '140px',
                                                                width: isMobile
                                                                    ? 'auto'
                                                                    : '210px',
                                                            }}
                                                        >
                                                            <FormAutocomplete
                                                                name={`prices[${dayIndex}].type`}
                                                                control={
                                                                    control
                                                                }
                                                                options={[
                                                                    {
                                                                        value: FeeType.EntireVenuePerNight,
                                                                        label: 'Per Night',
                                                                    },
                                                                    {
                                                                        value: FeeType.EntireVenuePerDay,
                                                                        label: 'Per Day',
                                                                    },
                                                                ]}
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                                {shouldShowHalfDayBilling &&
                                                    halfDayIndex !== null && (
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={4.5}
                                                            sx={{
                                                                display: 'flex',
                                                                width: isMobile
                                                                    ? 'auto'
                                                                    : '290px',
                                                                gap: '5px',
                                                            }}
                                                        >
                                                            <FormControl
                                                                size='small'
                                                                fullWidth={true}
                                                            >
                                                                <FormTextField
                                                                    id={`${space.feeId}-half-day__input`}
                                                                    name={`prices[${halfDayIndex}].unitPrice`}
                                                                    control={
                                                                        control
                                                                    }
                                                                    type='number'
                                                                    inputProps={{
                                                                        'aria-label': `${camelCaseToTitleCase(space.spaceType)} half day`,
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment:
                                                                            (
                                                                                <InputAdornment position='start'>
                                                                                    $
                                                                                </InputAdornment>
                                                                            ),
                                                                        endAdornment:
                                                                            (
                                                                                <InputAdornment position='end'>
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontSize:
                                                                                                '14px',
                                                                                        }}
                                                                                    >
                                                                                        Per
                                                                                        Half
                                                                                        Day
                                                                                    </Typography>
                                                                                </InputAdornment>
                                                                            ),
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}
                                            </Grid>
                                        );
                                    },
                                )}
                                {shouldShowHalfDayBilling && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                        }}
                                    >
                                        <InfoOutlinedIcon
                                            sx={{
                                                fontSize: '16px',
                                                color: 'text.secondary',
                                            }}
                                        />
                                        <Typography
                                            color='textPrimary'
                                            fontSize='12px'
                                            sx={{
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            To remove the half-day billing
                                            option, set the Min Hire Period to 1
                                            day.
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        )}
                    </form>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Box>
                        {experienceType !== 'commercial' && (
                            <Button onClick={handleClearWeekendPrice}>
                                Clear Custom Price
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            variant='contained'
                            type='submit'
                            form='base-price-modal-form'
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default BasePriceModal;
